import { PermissionsOfAdmin, ServiceTypes } from '@pharmaplan/common';

import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import {
  PrimaryAdminNavigation,
  Constants,
  SecondaryAdminNavigation,
} from '../../../helpers/Constants';
import strings from '../../../localization';

const isFullService = ServiceHandler.getService() === ServiceTypes.full;

const {
  adminCalendarDashboard,
  contactPreferences,
  pharmacyChains,
  pharmacistList,
  pharmacistCapacities,
  pharmacyList,
  incompatibleMatch,
  pendingActivationListPharmacist,
  pendingActivationListPharmacy,
  pendingActivationListDeletedUsers,
  pendingCancellation,
  updateIncompatibleMatch,
  bookingReport,
  pharmaplanStatistics,
  pendingActivationList,
  adminConfiguration,
  softwareList,
  availabilityReport,
  postedWorkshiftReport,
  requestedReport,
  createNewAdmin,
  createMultiple,
  createTechnicianMultiple,
  createStudentMultiple,
  updateAdmin,
  administratorList,
  listOfWorkshifts,
  adminMapDashboard,
  pharmacyListIncompatible,
  pharmacyListIncompatibleUpdate,
  viewRatings,
  pharmacyViewRatings,
  broadcastList,
  createBroadcastGroup,
  editBroadcastGroup,
  createNotification,
  editNotification,
  activityLogs,
  workshiftLogs,
  bookingLogs,
  availabilityLogs,
  broadcastListGroups,
  broadcastListNotification,
  pharmacyAdministrators,
  createPharmacyAdministrators,
  updatePharmacyAdministrators,
  appointPharmacyAdministrators,
  appointPharmacyList,
  adminChat,
  advertisingCampaigns,
  createAdvertisingCampaign,
  editAdvertisingCampaign,
  campaigns,
  createCampaign,
  editCampaign,
  configureCampaignEmail,
  campaignBookings,
  pharmacistLeads,
  technicianLeads,
  pharmacyLeads,
  studentLeads,
  createLead,
  linkedLeads,
  editLead,
  entitlements,
  editEntitlement,
  transactions,
  pendingRequests,
  techniciansList,
  pharmacyStudentList,
} = Constants.paths.admin;

const selfServiceOnlyPaths = [SecondaryAdminNavigation.transactions, SecondaryAdminNavigation.pendingRequests, SecondaryAdminNavigation.pharmacistStudentsList];
const fullServiceOnlyPaths = [SecondaryAdminNavigation.leads, SecondaryAdminNavigation.campaigns, SecondaryAdminNavigation.entitlements];

export const navigationConfig = (
  can: (params: PermissionsOfAdmin) => boolean,
  isSuperAdmin: boolean,
  isChatEnabled: boolean,
) => {
  const cannotViewAdministratorList = !can(
    PermissionsOfAdmin.AdministratorList,
  );
  const cannotViewLogs = !can(PermissionsOfAdmin.LogsView);
  const canViewExpressBooking = can(PermissionsOfAdmin.Booking);
  const canViewGlobalSettings = can(PermissionsOfAdmin.GlobalSetting);

  let dashboardSub = [
    SecondaryAdminNavigation.pharmacyList,
    SecondaryAdminNavigation.pharmacistsList,
    SecondaryAdminNavigation.techniciansList,
    SecondaryAdminNavigation.pharmacistStudentsList,
    SecondaryAdminNavigation.expressBooking,
    SecondaryAdminNavigation.pendingActivation,
    SecondaryAdminNavigation.pendingCancellation,
    SecondaryAdminNavigation.incompatibleMatch,
    SecondaryAdminNavigation.transactions,
    SecondaryAdminNavigation.pendingRequests,
  ];

  let adminListSub = [
    SecondaryAdminNavigation.administratorList,
    SecondaryAdminNavigation.broadcastNotifications,
    SecondaryAdminNavigation.pharmacyAdministrators,
    SecondaryAdminNavigation.pharmaplanStatistics,
    SecondaryAdminNavigation.advertisingCampaign,
    SecondaryAdminNavigation.campaigns,
    SecondaryAdminNavigation.leads,
    SecondaryAdminNavigation.entitlements,
  ];

  if (cannotViewAdministratorList) {
    adminListSub = adminListSub.filter(
      (item) =>
        item !== SecondaryAdminNavigation.administratorList,
    );
  }

  const configurationSub = [
    SecondaryAdminNavigation.pharmacistsCapacities,
    SecondaryAdminNavigation.softwareList,
    SecondaryAdminNavigation.pharmacyChains,
    SecondaryAdminNavigation.contactPreferences,
    SecondaryAdminNavigation.adminConfiguration,
  ];

  if (!canViewExpressBooking) {
    dashboardSub = dashboardSub.filter(
      (item) =>
        item !== SecondaryAdminNavigation.expressBooking,
    );
  }

  if (!isFullService) {
    adminListSub = adminListSub.filter((item) =>
      !fullServiceOnlyPaths.includes(item));
  }

  if (isFullService) {
    dashboardSub = dashboardSub.filter((item) =>
      !selfServiceOnlyPaths.includes(item));
  }

  let config = [
    {
      key: PrimaryAdminNavigation.dashboard,
      label: strings.dashboard,
      path: adminCalendarDashboard,
      subPaths: dashboardSub,
      subAbsolutePaths: [
        pharmacistList,
        techniciansList,
        pharmacyStudentList,
        createMultiple,
        createTechnicianMultiple,
        createStudentMultiple,
        pharmacyList,
        listOfWorkshifts,
        pharmacyListIncompatible,
        pharmacyListIncompatibleUpdate,
        viewRatings,
        pharmacyViewRatings,
        pendingActivationList,
        pendingCancellation,
        adminMapDashboard,
        incompatibleMatch,
        updateIncompatibleMatch,
        pendingActivationListPharmacy,
        pendingActivationListPharmacist,
        pendingActivationListDeletedUsers,
        pendingRequests,
        transactions,
      ],
    },
    {
      key: PrimaryAdminNavigation.admin,
      label: strings.admin,
      path: cannotViewAdministratorList ? broadcastList : administratorList,
      subPaths: adminListSub,
      subAbsolutePaths: [
        administratorList,
        createNewAdmin,
        updateAdmin,
        broadcastList,
        createBroadcastGroup,
        editBroadcastGroup,
        createNotification,
        editNotification,
        broadcastListGroups,
        broadcastListNotification,
        pharmacyAdministrators,
        createPharmacyAdministrators,
        updatePharmacyAdministrators,
        appointPharmacyAdministrators,
        appointPharmacyList,
        pharmaplanStatistics,
        createAdvertisingCampaign,
        advertisingCampaigns,
        editAdvertisingCampaign,
        campaigns,
        createCampaign,
        editCampaign,
        configureCampaignEmail,
        campaignBookings,
        pharmacistLeads,
        technicianLeads,
        pharmacyLeads,
        studentLeads,
        linkedLeads,
        createLead,
        editLead,
        entitlements,
        editEntitlement,
      ],
    },
    {
      key: PrimaryAdminNavigation.reports,
      label: strings.reports,
      path: bookingReport,
      subPaths: [
        SecondaryAdminNavigation.bookingReport,
        SecondaryAdminNavigation.availabilityReport,
        SecondaryAdminNavigation.requestedReport,
        SecondaryAdminNavigation.postedWorkshiftsReport,
      ],
      subAbsolutePaths: [
        bookingReport,
        availabilityReport,
        requestedReport,
        postedWorkshiftReport,
      ],
    },
    {
      key: PrimaryAdminNavigation.logs,
      label: strings.logs,
      path: activityLogs,
      subPaths: [
        SecondaryAdminNavigation.activityLogs,
        SecondaryAdminNavigation.workshiftLogs,
        SecondaryAdminNavigation.availabilityLogs,
        SecondaryAdminNavigation.bookingLogs,
      ],
      subAbsolutePaths: [
        activityLogs,
        workshiftLogs,
        availabilityLogs,
        bookingLogs,
      ],
    },
    {
      key: PrimaryAdminNavigation.configuration,
      label: strings.configuration,
      path: pharmacistCapacities,
      subPaths:
        isSuperAdmin || canViewGlobalSettings
          ? configurationSub
          : configurationSub.filter(
            (item) =>
              SecondaryAdminNavigation.adminConfiguration !== item,
          ),
      subAbsolutePaths: [
        Constants.paths.admin.softwareList,
        Constants.paths.admin.pharmacistCapacities,
        contactPreferences,
        Constants.paths.admin.adminConfiguration,
        pharmacyChains,
      ],
    },
    {
      key: PrimaryAdminNavigation.chat,
      label: 'Chat',
      path: adminChat,
      subPaths: [SecondaryAdminNavigation.chat],
      subAbsolutePaths: [adminChat],
    },

  ];

  if (cannotViewLogs) {
    config = config.filter((item) =>
      item.key !== PrimaryAdminNavigation.logs);
  }

  if (!isChatEnabled) {
    config = config.filter((item) =>
      item.key !== PrimaryAdminNavigation.chat);
  }

  return config;
};

export const secondaryNavigationConfig = (): {
  [key in SecondaryAdminNavigation]?: {
    label: string;
    path?: string;
    subPath?: Array<string>;
  };
} =>
  ({
  // Dashboard
    [SecondaryAdminNavigation.pharmaplanStatistics]: {
      label: strings.pharmaplanStatistics,
      path: pharmaplanStatistics,
    },
    [SecondaryAdminNavigation.expressBooking]: {
      label: strings.expressBooking,
    },
    [SecondaryAdminNavigation.pendingActivation]: {
      label: strings.pendingActivation,
      path: Constants.paths.admin.pendingActivationList,
      subPath: [pendingActivationListPharmacy,
        pendingActivationListPharmacist,
        pendingActivationListDeletedUsers],
    },
    [SecondaryAdminNavigation.pendingCancellation]: {
      label: strings.pendingCancellation,
      path: pendingCancellation,
    },

    // Admin
    [SecondaryAdminNavigation.pharmacistsList]: {
      label: strings.pharmacists,
      path: pharmacistList,
      subPath: [createMultiple, viewRatings],
    },
    [SecondaryAdminNavigation.pharmacyList]: {
      label: strings.pharmacies,
      path: Constants.paths.admin.pharmacyList,
      subPath: [
        listOfWorkshifts,
        pharmacyListIncompatible,
        pharmacyListIncompatibleUpdate,
        pharmacyViewRatings,
      ],
    },
    [SecondaryAdminNavigation.administratorList]: {
      label: strings.administrators,
      path: administratorList,
      subPath: [createNewAdmin, updateAdmin],
    },

    // Reports
    [SecondaryAdminNavigation.bookingReport]: {
      label: strings.bookingReport,
      path: bookingReport,
    },
    [SecondaryAdminNavigation.availabilityReport]: {
      label: strings.availabilityReport,
      path: availabilityReport,
    },
    [SecondaryAdminNavigation.requestedReport]: {
      label: strings.requestedReport,
      path: requestedReport,
    },
    [SecondaryAdminNavigation.postedWorkshiftsReport]: {
      label: strings.postedWorkshiftsReport,
      path: postedWorkshiftReport,
    },

    // Logs
    [SecondaryAdminNavigation.activityLogs]: {
      label: strings.activityLogs,
      path: activityLogs,
    },
    [SecondaryAdminNavigation.workshiftLogs]: {
      label: strings.workshiftLogs,
      path: workshiftLogs,
    },
    [SecondaryAdminNavigation.availabilityLogs]: {
      label: strings.availabilityLogs,
      path: availabilityLogs,
    },
    [SecondaryAdminNavigation.bookingLogs]: {
      label: strings.bookingLogs,
      path: bookingLogs,
    },

    // Configuration
    [SecondaryAdminNavigation.pharmacistsCapacities]: {
      label: strings.pharmacistsCapacities,
      path: pharmacistCapacities,
    },
    [SecondaryAdminNavigation.contactPreferences]: {
      label: strings.contact,
      path: contactPreferences,
    },
    [SecondaryAdminNavigation.pharmacyChains]: {
      label: strings.banners,
      path: pharmacyChains,
    },
    [SecondaryAdminNavigation.softwareList]: {
      label: strings.softwareSingular,
      path: softwareList,
    },
    [SecondaryAdminNavigation.adminConfiguration]: {
      label: strings.globalSettings,
      path: adminConfiguration,
    },
    [SecondaryAdminNavigation.broadcastNotifications]: {
      label: strings.broadcast,
      path: broadcastList,
      subPath: [
        createBroadcastGroup,
        editBroadcastGroup,
        createNotification,
        editNotification,
        broadcastListGroups,
        broadcastListNotification,
      ],
    },
    [SecondaryAdminNavigation.incompatibleMatch]: {
      label: strings.incompatibility,
      path: incompatibleMatch,
      subPath: [updateIncompatibleMatch],
    },
    [SecondaryAdminNavigation.pharmacyAdministrators]: {
      label: strings.pharmacyAdministrators,
      path: pharmacyAdministrators,
      subPath: [
        createPharmacyAdministrators,
        updatePharmacyAdministrators,
        appointPharmacyAdministrators,
        appointPharmacyList,
      ],
    },
    [SecondaryAdminNavigation.chat]: {
      label: strings.chat,
      path: adminChat,
    },
    [SecondaryAdminNavigation.advertisingCampaign]: {
      label: strings.advertising,
      path: advertisingCampaigns,
      subPath: [createAdvertisingCampaign, editAdvertisingCampaign],
    },
    [SecondaryAdminNavigation.campaigns]: {
      label: strings.campaigns,
      path: campaigns,
      subPath: [
        createCampaign,
        editCampaign,
        configureCampaignEmail,
        campaignBookings,
        linkedLeads,
      ],
    },
    [SecondaryAdminNavigation.leads]: {
      label: strings.leads,
      path: pharmacyLeads,
      subPath: [createLead, editLead, pharmacistLeads, technicianLeads, pharmacyLeads],
    },
    [SecondaryAdminNavigation.entitlements]: {
      label: strings.entitlements,
      path: entitlements,
      subPath: [editEntitlement],
    },
    [SecondaryAdminNavigation.transactions]: {
      label: strings.pharmagapTransactions,
      path: transactions,
    },
    [SecondaryAdminNavigation.pendingRequests]: {
      label: strings.pendingRequests,
      path: pendingRequests,
    },
    [SecondaryAdminNavigation.techniciansList]: {
      label: strings.technicians,
      path: techniciansList,
      subPath: [createTechnicianMultiple],
    },
    [SecondaryAdminNavigation.pharmacistStudentsList]: {
      label: strings.pharmacyStudent,
      path: pharmacyStudentList,
      subPath: [createStudentMultiple],
    },
  });
